/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, OnInit, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent } from '@angular/material/dialog';
import { ChatItem } from '@src/app/model/chat-item';
import { Follower } from '@src/app/model/user';
import { Field } from '@src/app/shared/field/Field';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { FormRichTextComponent } from '@src/app/shared/form/form-rich-text/form-rich-text.component';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { ChatField } from '@src/app/shared/chat/chat-field';
import { MatButtonModule } from '@angular/material/button';
import { ChatReplyComponent } from '@src/app/shared/chat/chat-reply/chat-reply.component';
import { ChatFollowersComponent } from '@src/app/shared/chat/chat-followers/chat-followers.component';
import { MatIconModule } from '@angular/material/icon';
import { CtlHolderComponent } from "@src/app/shared/form/ctl-holder/ctl-holder.component";

export interface ChatUpsertParameters {
    chatField: ChatField;
    replyTo: ChatItem;
    height?: number;
    width?: number;
}
@Component({
    selector: 'app-chat-upsert',
    templateUrl: './chat-upsert.component.html',
    styleUrls: ['./chat-upsert.component.scss'],
    imports: [MatDialogContent, MatIconModule, ChatFollowersComponent,
        ChatReplyComponent, MatButtonModule, CtlHolderComponent]
})
export class ChatUpsertComponent implements OnInit {
    data = inject<ChatUpsertParameters>(MAT_DIALOG_DATA);
    private isNarrowSvc = inject(IsNarrowService);
    private dialogRef = inject<MatDialogRef<ChatUpsertComponent>>(MatDialogRef);


    width = 500;
    height = 300;

    commentControl: AppFormControl = FormRichTextComponent.make('Comment', 'comment').makeControl() as AppFormControl;

    getTitle() {
        return 'Add new comment';
    }

    ngOnInit(): void {
        this.width = this.data.width ? this.data.width : this.isNarrowSvc.screenWidth * .75;
        this.height = this.data.height ? this.data.height : this.isNarrowSvc.screenHeight * .65;
    }

    closeDialog() {
        console.log('Close Dialog');
        this.dialogRef.close(null);
    }

    saveComment() {
        const chat = this.data.chatField.chat;
        if (Field.isEmpty(this.commentControl.value)) { return; }
        const newItem = { content: this.commentControl.value };
        if (this.data.replyTo) {
            newItem['parentId'] = this.data.replyTo.id;
        }
        chat.service.postChatItem(newItem, chat.parentId, true, chat.teamId).subscribe(comment => {
            if (comment?.id) {
                this.addCommentToChat(comment);
                this.finishSave(comment);
            }
        });
    }

    private addCommentToChat(comment: ChatItem) {
        const chat = this.data.chatField.chat;
        comment.replies = []; // new comment has no replies
        if (this.data.replyTo) { // might need to push it into parent...
            comment.parent = this.data.replyTo;
            if (!Array.isArray(this.data.replyTo.replies)) {
                this.data.replyTo.replies = [];
            }
            this.data.replyTo.replies.push(comment);
        }
        chat.hierarchyItems.push(comment);
        chat.comments.push(comment);
        if (!chat.followers.find((f) => f.personUserId === comment.authorId)) {
            chat.followers.push({
                personFullName: "You",
                personUserId: comment.authorId,
            } as Follower);
        }
    }

    // Run any post-save prompt (e.g. "put the request to waiting?") before closing, so the
    // refresh picks up status changes the prompt may have triggered server-side.
    private finishSave(comment: ChatItem) {
        const field = this.data.chatField;
        const done = () => {
            if (field.refreshAfterCommentFn) {
                field.refreshAfterCommentFn();
            }
            this.dialogRef.close(comment);
        };
        if (field.promptAfterSaveCommentFn) {
            field.promptAfterSaveCommentFn(comment).subscribe(() => done());
        } else {
            done();
        }
    }
}
